import React, {Component} from 'react';
import h from 'react-hyperscript';
import './LineGraph.scss';
import {unMountD3, getChartItemSlug, getPercentValue} from 'Src/utils/utils';
import * as d3 from 'd3';
import GraphTooltip from 'Components/GraphTooltip/GraphTooltip';

class LineGraph extends Component {
  constructor(props) {
    super(props);
    this.createLineGraph = this.createLineGraph.bind(this);
    this.margin = {
      top: 20,
      left: 35,
      bottom: 50,
      right: 35,
    };
    this.height = this.props.size[1] - this.margin.top - this.margin.bottom;
    this.ticksLabel = [0, .2, .4, .6, .8, 1];
    this.ticks = [.2, .4, .6, .8, 1];
    this.formatPercent = d3.format('.0%');
    this.state = {
      activeProducts: this.props.activeProducts,
    };

    this.tooltipRefs = {};

    this.initGraph();

    this.lineGraphRef = React.createRef();
  }

  componentDidMount() {
    this.setActiveProducts(true);
    const setRef = this.props.setRef;

    if (setRef) {
      setRef(this.lineGraphRef);
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.size !== this.props.size) {
      this.removeGraphElements();
      this.initGraph();
      this.setActiveProducts(true);
    }

    if (prevProps.activeProducts !== this.props.activeProducts) {
      this.setActiveProducts(false);
    }
  }

  removeGraphElements() {
    d3.select(this.node).selectAll('*').remove();
  }

  initGraph() {
    this.width = this.props.size[0] - this.margin.left - this.margin.right;

    this.n = 12;
    this.xScale = d3.scaleLinear()
        .domain([1, this.n])
        .range([0, this.width]);

    this.yScale = d3.scaleLinear()
        .domain([0, 1])
        .range([this.height, 0]);

    this.line = d3.line()
        .x((d, i) => this.xScale(i+1))
        .y((d) => this.yScale(d.y));
  }

  setActiveProducts(init) {
    this.setState({
      activeProducts: this.props.activeProducts,
    }, () => {
      if (init) {
        this.createLineGraph();
      } else {
        this.updateGraph();
      }
    });
  }

  setTooltipRef(id, ref) {
    this.tooltipRefs[id] = ref;
  }

  componentWillUnmount() {
    unMountD3(this.node, d3);
  }

  updateGraph() {
    const svg = d3.select(this.node);
    d3.scaleLinear()
        .domain([1, this.n])
        .range([0, this.width - 30]);

    svg.selectAll('.line').remove();

    this.drawActiveLines();
  }

  createLineGraph() {
    const xScale = this.xScale;
    const yScale = this.yScale;
    const svg = d3.select(this.node);

    svg.append('g')
        .attr('transform', `translate(
          ${this.margin.bottom}, ${this.margin.top})
        `);

    svg.append('g')
        .attr('class', 'x axis')
        .attr('transform', `translate(15, ${this.height + 20})`)
        .call(d3.axisBottom(xScale)
            .ticks(12)
            .tickFormat((d, i) => {
              return `Wk${i + 1}`;
            })
        );

    svg.append('g')
        .attr('class', 'y axis')
        .call(d3.axisRight(yScale)
            .tickValues(this.ticksLabel)
            .tickFormat(this.formatPercent))
        .attr('transform', `translate(${this.width + 20}, 5)`);

    svg.append('g')
        .attr('class', 'grid')
        .attr('transform', `translate(${this.width - 10}, 5)`)
        .attr('width', this.width - (this.margin.right + this.margin.left + 20))
        .call(
            d3.axisRight(yScale)
                .tickValues(this.ticks)
                .tickSize(-(this.width - 25))
                .tickFormat('')
        );

    this.drawActiveLines();
  }

  drawActiveLines() {
    const svg = d3.select(this.node);
    const line = this.line;
    const x = this.xScale;
    const setTooltip = this.setTooltip.bind(this);

    this.state.activeProducts.forEach((product, i) => {
      let dataset;
      this.props.filteredData.forEach((prod) => {
        if (product.id === prod.product) {
          dataset = prod.data;
        }
      });

      svg.append('path')
          .datum(dataset)
          .attr('class', `line line--${i}`)
          .attr('fill', 'none')
          .attr('stroke', product.color)
          .attr('stroke-linejoin', 'round')
          .attr('stroke-linecap', 'round')
          .attr('stroke-width', 2)
          .attr('d', line)
          .attr('transform', `translate(15, 5)`)
          .on('mouseover', () => {
            const slug = getChartItemSlug(dataset[i].productName);
            this.handleMouseOver(slug);
          })
          .on('mouseout', () => {
            const slug = getChartItemSlug(dataset[i].productName);
            this.handleMouseOut(slug);
          })
          .on('touchmove mousemove', function() {
            const slug = getChartItemSlug(dataset[i].productName);
            // eslint-disable-next-line no-invalid-this
            const x0 = x.invert(d3.mouse(this)[0]);
            const index = Math.floor(x0);
            const d0 = dataset[index - 1] || {x: 0, y: 0};

            // eslint-disable-next-line no-invalid-this
            const xPos = d3.mouse(this)[0] + 15;
            // eslint-disable-next-line no-invalid-this
            const yPos = d3.mouse(this)[1] + 5;
            const value = `${getPercentValue(d0.y)}%`;

            setTooltip(slug, {xPos, yPos, value});
          });
    });
  }

  handleMouseOver(slug) {
    const tooltip = this.tooltipRefs[slug];
    tooltip.makeActive();
  }

  handleMouseOut(slug) {
    const tooltip = this.tooltipRefs[slug];
    tooltip.makeInactive();
  }

  setTooltip(slug, {xPos, yPos, value}) {
    const tooltip = this.tooltipRefs[slug];
    const {width, height} = this.node.getBoundingClientRect();

    tooltip.setValue(value);
    if (tooltip) {
      tooltip.setPosition(xPos, yPos, {width, height}, {canInvert: true});
    }
  }


  render() {
    return (
      h('div', {
        id: this.props.id,
        className: 'line-graph',
        ref: this.lineGraphRef,
      },
      h('div', {
        className: 'line-graph__chart',
        style: {
          width: this.width + this.margin.left + this.margin.right,
          height: this.height + this.margin.top + this.margin.bottom,
        },
      }, [
        h('svg', {
          ref: (node) => this.node = node,
          width: this.width + this.margin.left + this.margin.right,
          height: this.height + this.margin.top + this.margin.bottom,
        }),
        h('div', {
          className: 'line-graph__tooltips',
        }, [...this.renderTooltips(this.state.activeProducts)]),
      ])
      )
    );
  }

  renderTooltips(activeProducts) {
    return activeProducts.map((product, i) => {
      const title = product.title;
      const icon = {
        standard: product.icon,
        retina: product.icon_2x,
      };
      const value = '0%';

      return h(GraphTooltip, {
        title, value, icon,
        id: getChartItemSlug(title),
        setRef: this.setTooltipRef.bind(this),
      });
    });
  }
}

export default LineGraph;
